import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Cookie, Settings, Check, ChevronUp, ChevronDown } from 'lucide-react';

// Translations for cookie banner
const translations = {
  en: {
    title: "We value your privacy",
    description: "We use cookies to enhance your browsing experience and analyze our traffic.",
    acceptAll: "Accept All",
    rejectAll: "Reject All",
    customize: "Customize",
    savePreferences: "Save Preferences",
    necessary: "Necessary",
    necessaryDesc: "Essential for the website to function. Cannot be disabled.",
    analytics: "Analytics",
    analyticsDesc: "Help us understand how visitors interact with our website.",
    marketing: "Marketing",
    marketingDesc: "Used to deliver personalized advertisements.",
    functional: "Functional",
    functionalDesc: "Enable enhanced functionality and personalization.",
    learnMore: "Learn more in our",
    privacyPolicy: "Privacy Policy",
    manageCookies: "Manage Cookies"
  },
  de: {
    title: "Wir schätzen Ihre Privatsphäre",
    description: "Wir verwenden Cookies, um Ihr Surferlebnis zu verbessern und unseren Datenverkehr zu analysieren.",
    acceptAll: "Alle akzeptieren",
    rejectAll: "Alle ablehnen",
    customize: "Anpassen",
    savePreferences: "Speichern",
    necessary: "Notwendig",
    necessaryDesc: "Essentiell für die Funktion der Website. Kann nicht deaktiviert werden.",
    analytics: "Analyse",
    analyticsDesc: "Helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.",
    marketing: "Marketing",
    marketingDesc: "Werden verwendet, um personalisierte Werbung zu liefern.",
    functional: "Funktional",
    functionalDesc: "Ermöglichen erweiterte Funktionalität und Personalisierung.",
    learnMore: "Mehr erfahren in unserer",
    privacyPolicy: "Datenschutzrichtlinie",
    manageCookies: "Cookies verwalten"
  },
  nl: {
    title: "Wij waarderen uw privacy",
    description: "Wij gebruiken cookies om uw browse-ervaring te verbeteren en ons verkeer te analyseren.",
    acceptAll: "Alles accepteren",
    rejectAll: "Alles weigeren",
    customize: "Aanpassen",
    savePreferences: "Opslaan",
    necessary: "Noodzakelijk",
    necessaryDesc: "Essentieel voor het functioneren van de website. Kan niet worden uitgeschakeld.",
    analytics: "Analyse",
    analyticsDesc: "Helpen ons te begrijpen hoe bezoekers met onze website omgaan.",
    marketing: "Marketing",
    marketingDesc: "Gebruikt om gepersonaliseerde advertenties te leveren.",
    functional: "Functioneel",
    functionalDesc: "Maakt verbeterde functionaliteit en personalisatie mogelijk.",
    learnMore: "Meer informatie in ons",
    privacyPolicy: "Privacybeleid",
    manageCookies: "Cookies beheren"
  },
  da: {
    title: "Vi værdsætter dit privatliv",
    description: "Vi bruger cookies for at forbedre din browsingoplevelse og analysere vores trafik.",
    acceptAll: "Accepter alle",
    rejectAll: "Afvis alle",
    customize: "Tilpas",
    savePreferences: "Gem",
    necessary: "Nødvendige",
    necessaryDesc: "Essentielle for at hjemmesiden kan fungere. Kan ikke deaktiveres.",
    analytics: "Analyse",
    analyticsDesc: "Hjælper os med at forstå, hvordan besøgende interagerer med vores hjemmeside.",
    marketing: "Marketing",
    marketingDesc: "Bruges til at levere personaliserede annoncer.",
    functional: "Funktionelle",
    functionalDesc: "Muliggør forbedret funktionalitet og personalisering.",
    learnMore: "Læs mere i vores",
    privacyPolicy: "Privatlivspolitik",
    manageCookies: "Administrer cookies"
  },
  "en-ca": {
    title: "We value your privacy",
    description: "We use cookies to enhance your browsing experience and analyze our traffic.",
    acceptAll: "Accept All",
    rejectAll: "Reject All",
    customize: "Customize",
    savePreferences: "Save Preferences",
    necessary: "Necessary",
    necessaryDesc: "Essential for the website to function. Cannot be disabled.",
    analytics: "Analytics",
    analyticsDesc: "Help us understand how visitors interact with our website.",
    marketing: "Marketing",
    marketingDesc: "Used to deliver personalized advertisements.",
    functional: "Functional",
    functionalDesc: "Enable enhanced functionality and personalization.",
    learnMore: "Learn more in our",
    privacyPolicy: "Privacy Policy",
    manageCookies: "Manage Cookies"
  }
};

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [showManageButton, setShowManageButton] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false
  });
  
  const location = useLocation();
  
  const getCurrentLang = () => {
    const pathParts = location.pathname.split('/');
    const lang = pathParts[1];
    if (['en', 'de', 'nl', 'da', 'en-ca'].includes(lang)) {
      return lang;
    }
    return 'en';
  };
  
  const lang = getCurrentLang();
  const t = translations[lang] || translations.en;

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setTimeout(() => setIsVisible(true), 500);
    } else {
      setShowManageButton(true);
      try {
        const savedPrefs = JSON.parse(consent);
        setPreferences(savedPrefs);
        updateGoogleConsent(savedPrefs);
      } catch (e) {
        setIsVisible(true);
      }
    }
  }, []);

  const updateGoogleConsent = (prefs) => {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'ad_storage': prefs.marketing ? 'granted' : 'denied',
        'ad_user_data': prefs.marketing ? 'granted' : 'denied',
        'ad_personalization': prefs.marketing ? 'granted' : 'denied',
        'analytics_storage': prefs.analytics ? 'granted' : 'denied',
        'functionality_storage': prefs.functional ? 'granted' : 'denied',
        'personalization_storage': prefs.functional ? 'granted' : 'denied'
      });
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = { necessary: true, analytics: true, marketing: true, functional: true };
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    updateGoogleConsent(allAccepted);
    setIsVisible(false);
    setShowCustomize(false);
    setShowManageButton(true);
  };

  const handleRejectAll = () => {
    const allRejected = { necessary: true, analytics: false, marketing: false, functional: false };
    setPreferences(allRejected);
    localStorage.setItem('cookieConsent', JSON.stringify(allRejected));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    updateGoogleConsent(allRejected);
    setIsVisible(false);
    setShowCustomize(false);
    setShowManageButton(true);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    updateGoogleConsent(preferences);
    setIsVisible(false);
    setShowCustomize(false);
    setShowManageButton(true);
  };

  const handleManageCookies = () => {
    setIsVisible(true);
    setShowCustomize(false);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return;
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!isVisible && !showManageButton) return null;

  return (
    <>
      {/* Manage Cookies Button */}
      {showManageButton && !isVisible && (
        <button
          onClick={handleManageCookies}
          className="fixed bottom-4 left-4 z-50 flex items-center gap-2 px-3 py-2 bg-zinc-800/90 hover:bg-zinc-700 text-white text-sm rounded-full backdrop-blur-sm border border-zinc-700 transition-all duration-200 shadow-lg"
          aria-label={t.manageCookies}
        >
          <Cookie className="w-4 h-4" />
          <span className="hidden sm:inline">{t.manageCookies}</span>
        </button>
      )}

      {/* Cookie Banner - Fixed bottom bar (non-blocking) */}
      {isVisible && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom duration-300">
          <div className="max-w-6xl mx-auto bg-zinc-900/95 backdrop-blur-md border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden">
            
            {/* Main Banner Content */}
            <div className="p-4 sm:p-5">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                
                {/* Text Section */}
                <div className="flex-1 flex items-start gap-3">
                  <div className="hidden sm:flex p-2 bg-purple-500/20 rounded-xl shrink-0">
                    <Cookie className="w-5 h-5 text-purple-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-semibold text-white mb-1">{t.title}</h2>
                    <p className="text-sm text-zinc-400 leading-relaxed">
                      {t.description}{' '}
                      <a 
                        href={`/${lang}/privacy-policy`} 
                        className="text-purple-400 hover:text-purple-300 underline whitespace-nowrap"
                      >
                        {t.privacyPolicy}
                      </a>
                    </p>
                  </div>
                </div>
                
                {/* Buttons Section */}
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 shrink-0">
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 sm:flex-none px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm font-medium rounded-xl transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Check className="w-4 h-4" />
                    {t.acceptAll}
                  </button>
                  
                  <button
                    onClick={handleRejectAll}
                    className="flex-1 sm:flex-none px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white text-sm font-medium rounded-xl transition-colors"
                  >
                    {t.rejectAll}
                  </button>
                  
                  <button
                    onClick={() => setShowCustomize(!showCustomize)}
                    className="flex-1 sm:flex-none px-4 py-2 border border-zinc-600 hover:border-zinc-500 text-zinc-300 hover:text-white text-sm font-medium rounded-xl transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Settings className="w-4 h-4" />
                    {t.customize}
                    {showCustomize ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                  </button>
                  
                  <button
                    onClick={handleRejectAll}
                    className="p-2 hover:bg-zinc-800 rounded-lg transition-colors sm:hidden"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5 text-zinc-400" />
                  </button>
                </div>
              </div>
            </div>

            {/* Customize Panel - Expandable */}
            {showCustomize && (
              <div className="px-4 sm:px-5 pb-4 border-t border-zinc-800 pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {/* Necessary */}
                  <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-xl">
                    <div className="min-w-0 pr-2">
                      <p className="text-sm font-medium text-white truncate">{t.necessary}</p>
                      <p className="text-xs text-zinc-500 truncate">{t.necessaryDesc}</p>
                    </div>
                    <div className="w-10 h-5 bg-purple-500 rounded-full flex items-center justify-end px-0.5 cursor-not-allowed opacity-70 shrink-0">
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </div>
                  </div>

                  {/* Analytics */}
                  <div 
                    className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-xl cursor-pointer hover:bg-zinc-800 transition-colors"
                    onClick={() => togglePreference('analytics')}
                  >
                    <div className="min-w-0 pr-2">
                      <p className="text-sm font-medium text-white truncate">{t.analytics}</p>
                      <p className="text-xs text-zinc-500 truncate">{t.analyticsDesc}</p>
                    </div>
                    <div className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors shrink-0 ${preferences.analytics ? 'bg-purple-500 justify-end' : 'bg-zinc-600 justify-start'}`}>
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </div>
                  </div>

                  {/* Marketing */}
                  <div 
                    className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-xl cursor-pointer hover:bg-zinc-800 transition-colors"
                    onClick={() => togglePreference('marketing')}
                  >
                    <div className="min-w-0 pr-2">
                      <p className="text-sm font-medium text-white truncate">{t.marketing}</p>
                      <p className="text-xs text-zinc-500 truncate">{t.marketingDesc}</p>
                    </div>
                    <div className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors shrink-0 ${preferences.marketing ? 'bg-purple-500 justify-end' : 'bg-zinc-600 justify-start'}`}>
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </div>
                  </div>

                  {/* Functional */}
                  <div 
                    className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-xl cursor-pointer hover:bg-zinc-800 transition-colors"
                    onClick={() => togglePreference('functional')}
                  >
                    <div className="min-w-0 pr-2">
                      <p className="text-sm font-medium text-white truncate">{t.functional}</p>
                      <p className="text-xs text-zinc-500 truncate">{t.functionalDesc}</p>
                    </div>
                    <div className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors shrink-0 ${preferences.functional ? 'bg-purple-500 justify-end' : 'bg-zinc-600 justify-start'}`}>
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </div>
                  </div>
                </div>
                
                {/* Save button when customizing */}
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleSavePreferences}
                    className="px-5 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm font-medium rounded-xl transition-colors flex items-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    {t.savePreferences}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
